// app/src/lib/auth/cognitoClient.js — Cognito Hosted UI / OAuth2 with PKCE.
//
// Why PKCE: we are a public client (browser); we cannot hold a client secret. PKCE
// binds the auth code to the device that initiated the flow, so a stolen code is
// useless without the original code_verifier (held in sessionStorage only for the
// duration of the redirect).
//
// Important security choices:
//   - `state` is 32 random bytes, base64url; verified on return.
//   - `nonce` is included in ID token requests; verified against the JWT.
//   - code_verifier lives in sessionStorage ONLY for the redirect window, then deleted.
//   - All tokens land in IndexedDB via tokenStore; never localStorage.
//   - JWTs are signature-verified against the User Pool JWKS before trust.

import { b64uEncode } from '../crypto.js';
import { getConfig } from '../config.js';
import * as kv from '../idbKv.js';
import { setTokens, getTokens, clearTokens } from '../tokenStore.js';
import { verifyJwt, unsafeDecodePayload } from './jwt.js';

const PKCE_KEY = 'kanjo.pkce.v1'; // sessionStorage scope; deleted after redirect.
const SALT_KEY = 'kanjo.crypto.salt.v1';
const SALT_BYTES = 32;

let refreshTimer = null;

/** Build the hosted-UI URL (login or signup) with PKCE + state stashed.
 *  `loginHint` (optional) pre-fills the email field on the managed login /
 *  sign-up page (OIDC `login_hint`) so a user handed off from the /p rolodex
 *  flow doesn't retype the email they already gave. */
async function hostedUiUrl(path, provider, loginHint) {
  const cfg = getConfig();
  const codeVerifier = b64uEncode(crypto.getRandomValues(new Uint8Array(32)));
  const codeChallenge = b64uEncode(
    new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))),
  );
  const state = b64uEncode(crypto.getRandomValues(new Uint8Array(32)));
  const nonce = b64uEncode(crypto.getRandomValues(new Uint8Array(16)));

  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ codeVerifier, state, nonce }));

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.cognitoClientId,
    redirect_uri: cfg.redirectUri,
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
    state,
    nonce,
  });
  if (provider) params.set('identity_provider', provider); // "Google" | "SignInWithApple" | "Facebook"
  if (loginHint) params.set('login_hint', loginHint);
  return `${cfg.cognitoDomain}${path}?${params}`;
}

/** Initiate the OAuth code flow on the hosted LOGIN page. Redirects the browser. */
export async function signIn(provider, loginHint) {
  window.location.assign(await hostedUiUrl('/oauth2/authorize', provider, loginHint));
}

/**
 * Send a new user to create an account. We use /oauth2/authorize (NOT the legacy
 * /signup deep-link, which errors under Cognito Managed Login) — the managed
 * login page exposes a "Sign up" link when self-registration is enabled.
 */
export async function signUp(provider, loginHint) {
  window.location.assign(await hostedUiUrl('/oauth2/authorize', provider, loginHint));
}

/**
 * Complete the OAuth flow when the browser returns to redirectUri with `?code=...&state=...`.
 * Returns the verified ID-token claims on success; throws on any mismatch.
 */
export async function completeSignIn() {
  const cfg = getConfig();
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  const error = url.searchParams.get('error');
  if (error) throw new Error(`cognito: ${error}`);
  if (!code || !state) throw new Error('cognito: missing code or state');

  const stored = sessionStorage.getItem(PKCE_KEY);
  if (!stored) throw new Error('cognito: missing PKCE state (different tab or expired)');
  sessionStorage.removeItem(PKCE_KEY); // single use.
  const { codeVerifier, state: storedState, nonce } = JSON.parse(stored);
  if (state !== storedState) throw new Error('cognito: state mismatch');

  const tokenRes = await fetch(`${cfg.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cfg.cognitoClientId,
      redirect_uri: cfg.redirectUri,
      code,
      code_verifier: codeVerifier,
    }),
  });
  if (!tokenRes.ok) throw new Error(`cognito: token exchange ${tokenRes.status}`);
  const body = await tokenRes.json();

  const claims = await verifyAndStore(body, nonce);
  scheduleRefresh();
  // Strip OAuth params from the URL so a refresh doesn't replay the (one-time) code.
  history.replaceState({}, '', url.pathname + url.hash);
  return claims;
}

/** Return verified ID-token claims, or null if not signed in. */
export async function currentClaims() {
  const salt = await getSalt();
  const tokens = await getTokens(salt);
  if (!tokens) return null;
  return unsafeDecodePayload(tokens.idToken); // Already verified at store time; safe.
}

/** Return the current access token (for API calls), refreshing if needed. */
export async function getAccessToken() {
  const salt = await getSalt();
  let tokens = await getTokens(salt);
  if (!tokens) return null;
  if (tokens.accessExpiresAt - Date.now() < 60_000) {
    tokens = await refresh();
    if (!tokens) return null;
  }
  return tokens.accessToken;
}

/**
 * Force a token refresh now (re-runs the pre-token-gen trigger), so claims that
 * changed server-side — e.g. custom:accountId / custom:role after accepting a
 * team invite — take effect without waiting for natural expiry. Returns the new
 * claims or null.
 */
export async function forceRefresh() {
  const tokens = await refresh();
  return tokens ? tokens.claims : null;
}

export async function signOut() {
  const cfg = getConfig();
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
  // Local-first (per product decision): do NOT wipe the local books on sign-out.
  // The owner's data persists in IndexedDB so a same-device re-login is instant
  // with no S3 round-trip; the cloud blob is only consulted for new-device /
  // recovery (local empty) and for read-only viewers (who re-pull at login).
  // Trade-off accepted: on a shared device, cached books linger until a
  // different user signs in (queries are still scoped to the signed-in userId,
  // and a viewer login clears+reloads the shared tables).
  await clearTokens();
  // Federated sign-out at Cognito so a returning visitor doesn't get auto-signed-in.
  const params = new URLSearchParams({ client_id: cfg.cognitoClientId, logout_uri: cfg.redirectUri });
  window.location.assign(`${cfg.cognitoDomain}/logout?${params}`);
}

// ── internals ──────────────────────────────────────────────────────────────

async function refresh() {
  const cfg = getConfig();
  const salt = await getSalt();
  const tokens = await getTokens(salt);
  if (!tokens) return null;
  const res = await fetch(`${cfg.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cfg.cognitoClientId,
      refresh_token: tokens.refreshToken,
    }),
  });
  if (!res.ok) {
    await clearTokens();
    return null;
  }
  const body = await res.json();
  // Refresh response often omits refresh_token; keep the old one.
  body.refresh_token = body.refresh_token || tokens.refreshToken;
  const claims = await verifyAndStore(body, null);
  scheduleRefresh();
  return { idToken: body.id_token, accessToken: body.access_token, refreshToken: body.refresh_token, accessExpiresAt: Date.now() + (body.expires_in * 1000), issuedAt: Date.now(), claims };
}

function scheduleRefresh() {
  if (refreshTimer) clearTimeout(refreshTimer);
  // Fire two minutes before expiry. Read tokens lazily so we always honor latest.
  getSalt().then(getTokens).then((t) => {
    if (!t) return;
    const delay = Math.max(5_000, t.accessExpiresAt - Date.now() - 120_000);
    refreshTimer = setTimeout(() => { refresh().catch(() => {}); }, delay);
  });
}

async function verifyAndStore(body, expectedNonce) {
  const cfg = getConfig();
  if (!body.id_token || !body.access_token) throw new Error('cognito: incomplete token response');
  const idClaims = await verifyJwt(body.id_token, cfg.cognitoJwksUrl, cfg.cognitoIssuer, cfg.cognitoClientId);
  if (expectedNonce && idClaims.nonce !== expectedNonce) throw new Error('cognito: nonce mismatch');
  // Access token: verify too — required for API calls.
  await verifyJwt(body.access_token, cfg.cognitoJwksUrl, cfg.cognitoIssuer, cfg.cognitoClientId);

  const salt = await getSalt();
  /** @type {import('../tokenStore.js').Tokens} */
  const tokens = {
    idToken: body.id_token,
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    accessExpiresAt: Date.now() + (body.expires_in * 1000),
    issuedAt: Date.now(),
  };
  await setTokens(tokens, salt);
  return idClaims;
}

async function getSalt() {
  let salt = await kv.get(SALT_KEY);
  if (!(salt instanceof Uint8Array) || salt.length !== SALT_BYTES) {
    salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
    await kv.set(SALT_KEY, salt);
  }
  return salt;
}
